import { createFileRoute } from '@tanstack/react-router'
import type { ColDef } from 'ag-grid-community'
import { useMemo, useState } from 'react'

import type { PublishableKey } from '@/api/admin-sales-channels-types'
import { preloadListPublishableKeys, useListPublishableKeys } from '@/api/react-query'
import { CreateAccessKeyDrawer } from '@/components/access-key/create-access-key-drawer'
import { Container, ContainerHeader } from '@/components/base/container'
import { Copy } from '@/components/base/copy'
import { EmptyState } from '@/components/base/empty-state'
import { IconButton } from '@/components/base/icon-button'
import { SingleColumnPage } from '@/components/page-layout'
import { Table } from '@/components/table/table'
import { useI18n } from '@/i18n/i18n-react'
import { Plus } from '@/icons/plus'

export const Route = createFileRoute('/_authenticated/settings/access-keys/')({
	component: AccessKeysList,
	loader: async ({ context }) => await preloadListPublishableKeys(context, { query: {} }),
	staticData: {
		crumb: ({ context: { L } }) => L.Settings.AccessKeys.Title()
	}
})

type EditorState = { mode: 'create' } | { mode: 'edit'; key: PublishableKey } | null

function AccessKeysList() {
	const { L } = useI18n()
	const publishableKeys = useListPublishableKeys({})
	const [editor, setEditor] = useState<EditorState>(null)

	const columnDefs = useMemo<ColDef<PublishableKey>[]>(
		() => [
			{
				headerName: L.Fields.Name(),
				field: 'name',
				minWidth: 150,
				flex: 2
			},
			{
				headerName: L.Settings.AccessKeys.Column.Key(),
				minWidth: 180,
				flex: 2,
				cellRenderer: ({ data }: { data: PublishableKey }) => {
					if (!data) return null
					return (
						<div className='flex h-full items-center gap-2'>
							<code className='rounded bg-ui-bg-subtle px-2 py-1 text-xs'>
								{`${data.token.substring(0, 20)}...`}
							</code>
							<Copy content={data.token} variant='mini' />
						</div>
					)
				}
			},
			{
				headerName: L.Settings.AccessKeys.Column.SalesChannels(),
				minWidth: 160,
				flex: 2,
				valueGetter: ({ data }) =>
					(data?.salesChannels ?? []).map(sc => sc.name).join(', ') || L.Settings.AccessKeys.None()
			},
			{
				headerName: L.Settings.AccessKeys.Column.MarketingChannel(),
				minWidth: 140,
				flex: 1,
				valueGetter: ({ data }) => data?.marketingChannel?.name ?? L.Settings.AccessKeys.None()
			},
			{
				headerName: L.Fields.CreatedAt(),
				field: 'createdAt',
				type: 'date',
				minWidth: 120,
				flex: 1
			}
		],
		[L]
	)

	return (
		<SingleColumnPage>
			<Container className='flex flex-1 flex-col overflow-hidden'>
				<ContainerHeader
					heading={L.Settings.AccessKeys.Title()}
					rightSide={
						<IconButton
							tooltipText={L.Settings.AccessKeys.Create()}
							onClick={() => setEditor({ mode: 'create' })}
						>
							<Plus />
						</IconButton>
					}
				/>
				<div className='min-w-0 flex-1 overflow-x-auto'>
					<Table<PublishableKey>
						className='w-full min-w-[700px] flex-1'
						data={publishableKeys.data}
						columnUtilities={{}}
						columnDefs={columnDefs}
						suppressCellFocus
						onRowClicked={event => {
							if (event.data) setEditor({ mode: 'edit', key: event.data })
						}}
						emptyState={
							<EmptyState
								title={L.Settings.AccessKeys.Empty.Title()}
								description={L.Settings.AccessKeys.Empty.Description()}
								className='min-h-[200px]'
								action={{
									label: L.Settings.AccessKeys.Create(),
									onClick: () => setEditor({ mode: 'create' })
								}}
							/>
						}
					/>
				</div>
			</Container>

			{editor && (
				<CreateAccessKeyDrawer
					key={editor.mode === 'edit' ? editor.key.uuid : 'create'}
					open
					onOpenChange={open => {
						if (!open) setEditor(null)
					}}
					manageLinks
					editKey={editor.mode === 'edit' ? editor.key : null}
				/>
			)}
		</SingleColumnPage>
	)
}
